import { View } from "react-native";
import { Episode } from "../common/episode";
import { Text } from "react-native-paper";
import React from "react";
import { LanguageContext } from "@atgof-common/language";
import {
  countLabel,
  formatTimestamp,
  scriptPagesDescription,
} from "@atgof-common/util";
import FixtureLabel from "@atgof-common/FixtureLabel";
import { FieldValue } from "@atgof-firebase/firebase";
import { Editable } from "./Editable";
import { CompletionIndicator } from "@atgof-common/CompletionIndicator";
import { DateEditor } from "./DateEditor";

function EpisodeEditor({ episode }: { episode: Episode }) {
  const { ref, txDate } = episode;
  const { ph } = React.useContext(LanguageContext);
  function setTxDate(date: Date | undefined) {
    ref.update({ txDate: date ?? FieldValue.delete() });
  }
  return (
    <View
      style={{
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 16,
        alignItems: "center",
      }}
    >
      <DateEditor
        label={ph("tx-date") as string}
        value={txDate?.toDate()}
        onChange={setTxDate}
      />
      <View>
        <FixtureLabel text={ph("director") as string} />
        <Editable
          text={episode.director ?? ""}
          onChange={(director) => ref.update({ director })}
          paperTheme
        />
      </View>
      <View>
        <FixtureLabel text={ph("scriptwriter") as string} />
        <Editable
          text={episode.writer ?? ""}
          onChange={(writer) => ref.update({ writer })}
          paperTheme
        />
      </View>
    </View>
  );
}

export function EpisodeInfo({
  episode,
  readonly = true,
}: {
  episode: Episode;
  readonly?: boolean;
}) {
  const { ph } = React.useContext(LanguageContext);
  const {
    txDate,
    sceneCount,
    director,
    writer,
    isComplete,
    pendingActionsForScenes,
    progress,
  } = episode;
  if (!readonly) return <EpisodeEditor episode={episode} />;
  const showProgress =
    !isComplete && (progress?.complete.n || progress?.complete.p?.n);
  const [totalPages, completePages, incompletePages] = (
    ["total", "complete", "incomplete"] as (
      | "total"
      | "complete"
      | "incomplete"
    )[]
  ).map((k) =>
    showProgress && progress[k] ? parseInt(progress[k].pages) : undefined,
  );
  return (
    <View
      style={{
        flex: 1,
        gap: 4,
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignContent: "center",
          gap: 4,
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        {txDate ? (
          <View style={{ gap: 4, flexDirection: "row", alignItems: "center" }}>
            <FixtureLabel text={ph("tx") as string} small />
            <Text>{formatTimestamp(txDate)} </Text>
          </View>
        ) : null}
        {writer ? (
          <View style={{ gap: 4, flexDirection: "row", alignItems: "center" }}>
            <FixtureLabel text={ph("script") as string} small />
            <Text>{writer}</Text>
          </View>
        ) : null}
        {director ? (
          <View style={{ gap: 4, flexDirection: "row", alignItems: "center" }}>
            <FixtureLabel text={ph("director-short") as string} small />
            <Text>{director}</Text>
          </View>
        ) : null}
      </View>
      <View
        style={{
          flexDirection: "row",
          alignContent: "center",
          alignItems: "center",
          gap: 4,
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
          }}
        >
          <View style={{ marginRight: 8 }}>
            <CompletionIndicator
              isComplete={isComplete ?? false}
              hasPendingActions={pendingActionsForScenes?.length ? true : false}
            />
          </View>
          {showProgress ? (
            <FixtureLabel small text={ph("total") as string} />
          ) : null}
          <Text>
            {countLabel(
              ph,
              "count-scene",
              progress ? progress.total.n : sceneCount,
            )}
          </Text>
          {progress ? (
            <Text style={{ textDecorationLine: "underline" }}>
              {scriptPagesDescription(progress.total.pages)}
            </Text>
          ) : null}
        </View>
        {showProgress ? (
          <>
            <View
              style={{ flexDirection: "row", alignItems: "center", gap: 4 }}
            >
              <FixtureLabel small text={ph("completed") as string} />
              <Text>
                {(countLabel(ph, "count-scene", progress.complete.n, false) ??
                  "") +
                  (progress.complete.p?.n
                    ? " " + countLabel(ph, "count-part", progress.complete.p?.n)
                    : "")}
              </Text>
              <Text style={{ textDecorationLine: "underline" }}>
                {scriptPagesDescription(progress.complete.pages)}
              </Text>
            </View>
            {totalPages != null && completePages != null ? (
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <FixtureLabel small text={ph("remaining") as string} />
                <Text>
                  {progress.incomplete
                    ? (countLabel(
                        ph,
                        "count-scene",
                        progress.incomplete.n,
                        false,
                      ) ?? "") +
                      (progress.incomplete.p?.n
                        ? " " +
                          countLabel(ph, "count-part", progress.incomplete.p?.n)
                        : "")
                    : countLabel(
                        ph,
                        "count-scene",
                        progress.total.n - progress.complete.n,
                      )}
                </Text>
                <Text style={{ textDecorationLine: "underline" }}>
                  {scriptPagesDescription(
                    incompletePages == null
                      ? totalPages - completePages
                      : incompletePages,
                  )}
                </Text>
              </View>
            ) : null}
          </>
        ) : null}
      </View>
    </View>
  );
}
